import { Box, Button, IconButton, Menu, MenuItem, Stack, Tooltip } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useFlags } from "launchdarkly-react-client-sdk";
import { MouseEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { crmLiteUpgradeMessage } from "../../../../Constants/CrmLiteConstants";
import { useCrmLiteClient } from "../../../../State/Slices/SelectedClientSlice";
import { isRouteWithAbacAllowed } from "../../../../State/Hooks/crmLiteHooks";
import { IFeatureToggles } from "../../../../Typings/FeatureToggles";
import DropDownMenuButton, { DropDownMenuOption } from "../DropDownMenuButton/DropDownMenuButton";
import EmailIcon from "@mui/icons-material/Email";
import SmsIcon from "@mui/icons-material/Sms";
import { LibraryBooks } from "@mui/icons-material";

const pages = [
  {
    path: "/",
    label: "Dashboard",
    disabled: false,
    testAttribute: "home",
  },
  {
    path: "/candidates",
    label: "Candidates",
    disabled: false,
    testAttribute: "candidatemanagement",
  },
  {
    path: "/candidategroups",
    label: "Groups",
    disabled: false,
    testAttribute: "candidategroups",
  },
  {
    //TODO: This is just until we decide on where the campaigns will live
    path: "/leadcapture/forms",
    label: "Lead Capture",
    disabled: false,
    testAttribute: "leadcapture",
  },
  {
    path: "/messaging",
    multi: true,
    label: "Messaging Center",
    disabled: false,
    testAttribute: "messagecenter",
    options: [
      { label: "Email Marketing", path: "/messaging/email", icon: <EmailIcon />, disabled: false },
      {
        label: "Text Messages",
        path: "/messaging/text",
        icon: <SmsIcon sx={{ transform: "scaleX(-1)" }} />,
        disabled: false,
        followedByDivider: true,
      },
      {
        label: "Communication Templates",
        path: "/messaging/templates",
        icon: <LibraryBooks />,
        disabled: false,
      },
    ],
  },
];

export function DesktopNavMenu() {
  const isCrmLiteClient = useCrmLiteClient();
  const { enableAbacFeatures } = useFlags<IFeatureToggles>();
  return (
    <Stack
      direction="row"
      component="nav"
      id="nav-menu"
      aria-label="Page Navigation"
      sx={{
        display: { xs: "none", lg: "flex" },
        "& a": {
          textDecoration: "none",
        },
      }}
    >
      {pages.map((page) => {
        const isCrmLitePage = isRouteWithAbacAllowed(enableAbacFeatures, page.path);
        const pageDisabled = page.disabled || (isCrmLiteClient && !isCrmLitePage);
        if (page.multi) {
          return pageDisabled ? (
            <Tooltip title={crmLiteUpgradeMessage} key={page.path}>
              <Box
                sx={{
                  "& .Mui-disabled": {
                    backgroundColor: "transparent !important",
                    color: "rgba(255,255,255,0.5) !important",
                  },
                  display: "flex",
                }}
              >
                <DropDownMenuButton
                  id={page.testAttribute}
                  key={page.path}
                  title={page.label}
                  data-test={`nav-menu-${page.testAttribute}`}
                  disabled={true}
                  options={page.options.map((c) => {
                    return {
                      title: c.label,
                      icon: c.icon,
                      disabled: true,
                      path: c.path,
                      followedByDivider: c.followedByDivider,
                    } as DropDownMenuOption;
                  })}
                />
              </Box>
            </Tooltip>
          ) : (
            <DropDownMenuButton
              id={page.testAttribute}
              key={page.path}
              title={page.label}
              data-test={`nav-menu-${page.testAttribute}`}
              disabled={page.disabled}
              options={page.options.map((c, i) => {
                return {
                  title: c.label,
                  icon: c.icon,
                  disabled: c.disabled,
                  path: c.path,
                  followedByDivider: c.followedByDivider && i < page.options.length - 1, // Do not add divider to the last item
                } as DropDownMenuOption;
              })}
            />
          );
        } else if (pageDisabled) {
          const message = !isCrmLitePage ? crmLiteUpgradeMessage : `${page.label} coming soon!`;
          return (
            <Button
              data-test={`nav-menu-${page.testAttribute}`}
              key={page.path}
              disabled={true}
              title={message}
              sx={{
                my: 2,
                color: "white",
                display: "block",
                textDecoration: "none",
                "&.Mui-disabled": {
                  color: "white",
                  opacity: 0.5,
                  pointerEvents: "initial",
                },
              }}
            >
              {page.label}
            </Button>
          );
        } else {
          return (
            <Button
              data-test={`nav-menu-${page.testAttribute}`}
              key={page.path}
              component={Link}
              to={page.path}
              disabled={page.disabled}
              sx={{
                my: 2,
                color: "white",
                display: "block",
                textDecoration: "none",
              }}
            >
              {page.label}
            </Button>
          );
        }
      })}
    </Stack>
  );
}

export function MobileNavMenu() {
  const isCrmLiteClient = useCrmLiteClient();
  const { enableAbacFeatures } = useFlags<IFeatureToggles>();
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const handleOpenNavMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };
  const navigate = useNavigate();
  return (
    <Box
      component="nav"
      id="nav-menu-mobile"
      aria-label="Page Navigation"
      sx={{
        flex: { xs: "none", lg: 1 },
        display: { xs: "flex", lg: "none" },
        px: { xs: 0, lg: 2 },
        ml: "auto",
      }}
    >
      <IconButton
        data-test="nav-menu-toggle"
        size="large"
        aria-label="Toggle Navigation Menu"
        aria-controls="menu-mobile-nav"
        aria-haspopup="true"
        onClick={handleOpenNavMenu}
        color="inherit"
      >
        <MenuIcon />
      </IconButton>
      <Menu
        id="menu-mobile-nav"
        anchorEl={anchorElNav}
        open={Boolean(anchorElNav)}
        onClose={handleCloseNavMenu}
        variant="menu"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          display: { xs: "block", lg: "none" },
        }}
        slotProps={{
          paper: { sx: { width: 1 } },
        }}
        MenuListProps={{
          sx: {
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "center",
          },
        }}
      >
        {pages.map((page) => {
          const isCrmLitePage = isRouteWithAbacAllowed(enableAbacFeatures, page.path);
          const pageDisabled = page.disabled || (isCrmLiteClient && !isCrmLitePage);

          return page.multi && page.options !== undefined ? (
            <MenuItem
              data-test={`nav-menu-mobile-${page.testAttribute}`}
              key={page.path}
              disabled={pageDisabled}
              role="none"
              tabIndex={undefined}
              sx={{ textAlign: "center", padding: 0 }}
            >
              <DropDownMenuButton
                id={page.testAttribute}
                key={page.path}
                title={page.label}
                data-test={`nav-menu-${page.testAttribute}`}
                disabled={pageDisabled || page.disabled}
                mobile={true}
                options={page.options.map((c, i) => {
                  return {
                    title: c.label,
                    icon: c.icon,
                    disabled: pageDisabled || c.disabled,
                    path: c.path,
                    followedByDivider: c.followedByDivider && i < page.options.length - 1, // Do not add divider to the last item
                  } as DropDownMenuOption;
                })}
                onClick={handleCloseNavMenu}
              />
            </MenuItem>
          ) : (
            <MenuItem
              data-test={`nav-menu-mobile-${page.testAttribute}`}
              key={page.path}
              disabled={pageDisabled}
              onClick={() => {
                navigate(page.path);
                handleCloseNavMenu();
              }}
              role="none"
              tabIndex={undefined}
              sx={{
                display: "block",
                width: 1,
                p: 0,
                minHeight: 0,
                "& a": {
                  display: "block",
                  width: 1,
                  px: 3,
                  py: 1,
                  m: 0,
                  textDecoration: "none",
                  textAlign: "center",
                  color: "text.primary",
                  fontWeight: "bold",
                },
              }}
            >
              <Link onClick={handleCloseNavMenu} to={page.path} role="menuitem">
                {page.label}
              </Link>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}
